import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { can } from '@itsappy/shared';
import { onUnauthorized } from '@/lib/api';
import { useTimezoneSync } from '@/lib/time';
import { useAuth } from '@/store/auth';
import { ConfirmProvider, Toaster } from '@/components/ui';
import { BrandLoader } from '@/components/BrandLoader';
import { AppShell } from '@/components/layout/AppShell';
import {
  effectiveProductSurface,
  featureHome,
  getProductSurface,
  hasFullAccess,
  productHome,
} from '@/lib/product';

// Route code is loaded only when it is needed. Chat, meetings, the office
// renderer and games are substantial surfaces; putting all of them in the
// first bundle made every navigation pay their parse/compile cost up front.

// The pre-login front door: what the product is, with the sign-in and sign-up
// forms as a dialog over it. `/login` and `/register` stay real URLs — an
// expired session deep in the app still redirects to one of them.
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const SpacesPage = lazy(() =>
  import('@/pages/SpacesPage').then((m) => ({ default: m.SpacesPage })),
);
const SpacePage = lazy(() =>
  import('@/pages/SpacePage').then((m) => ({ default: m.SpacePage })),
);
const ProjectsPage = lazy(() =>
  import('@/pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const RepositoriesPage = lazy(() =>
  import('@/pages/RepositoriesPage').then((m) => ({ default: m.RepositoriesPage })),
);
const BoardPage = lazy(() =>
  import('@/pages/BoardPage').then((m) => ({ default: m.BoardPage })),
);
const MyTasksPage = lazy(() =>
  import('@/pages/MyTasksPage').then((m) => ({ default: m.MyTasksPage })),
);
const AutomationsPage = lazy(() =>
  import('@/pages/AutomationsPage').then((m) => ({ default: m.AutomationsPage })),
);
const ChatPage = lazy(() => import('@/pages/ChatPage').then((m) => ({ default: m.ChatPage })));
const InboxPage = lazy(() =>
  import('@/pages/InboxPage').then((m) => ({ default: m.InboxPage })),
);
const FilesPage = lazy(() =>
  import('@/pages/FilesPage').then((m) => ({ default: m.FilesPage })),
);
const WhiteboardsPage = lazy(() =>
  import('@/pages/WhiteboardsPage').then((m) => ({ default: m.WhiteboardsPage })),
);
const WhiteboardPage = lazy(() =>
  import('@/pages/WhiteboardPage').then((m) => ({ default: m.WhiteboardPage })),
);
const MeetingsPage = lazy(() =>
  import('@/pages/MeetingsPage').then((m) => ({ default: m.MeetingsPage })),
);
const CalendarPage = lazy(() =>
  import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);
const MeetingRoomPage = lazy(() =>
  import('@/pages/MeetingRoomPage').then((m) => ({ default: m.MeetingRoomPage })),
);
const ClientPortalPage = lazy(() =>
  import('@/pages/ClientPortalPage').then((m) => ({ default: m.ClientPortalPage })),
);
const GuestMeetingPage = lazy(() =>
  import('@/pages/GuestMeetingPage').then((m) => ({ default: m.GuestMeetingPage })),
);
const CallRoomPage = lazy(() =>
  import('@/pages/CallRoomPage').then((m) => ({ default: m.CallRoomPage })),
);
const GamesPage = lazy(() =>
  import('@/pages/GamesPage').then((m) => ({ default: m.GamesPage })),
);
const GamePage = lazy(() => import('@/pages/GamePage').then((m) => ({ default: m.GamePage })));
const SoloGamePage = lazy(() =>
  import('@/pages/SoloGamePage').then((m) => ({ default: m.SoloGamePage })),
);
const PeoplePage = lazy(() =>
  import('@/pages/PeoplePage').then((m) => ({ default: m.PeoplePage })),
);
const EngagementPage = lazy(() =>
  import('@/pages/EngagementPage').then((m) => ({ default: m.EngagementPage })),
);
const RecapPage = lazy(() =>
  import('@/pages/RecapPage').then((m) => ({ default: m.RecapPage })),
);
const LearningPage = lazy(() =>
  import('@/pages/LearningPage').then((m) => ({ default: m.LearningPage })),
);
const InsightsPage = lazy(() =>
  import('@/pages/InsightsPage').then((m) => ({ default: m.InsightsPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const TeamPage = lazy(() =>
  import('@/pages/TeamPage').then((m) => ({ default: m.TeamPage })),
);
const CompanyPage = lazy(() =>
  import('@/pages/CompanyPage').then((m) => ({ default: m.CompanyPage })),
);
const WorkspaceRequestsPage = lazy(() =>
  import('@/pages/WorkspaceRequestsPage').then((m) => ({ default: m.WorkspaceRequestsPage })),
);
const PlatformWorkspacesPage = lazy(() =>
  import('@/pages/PlatformWorkspacesPage').then((m) => ({ default: m.PlatformWorkspacesPage })),
);
const PlatformSpacesPage = lazy(() =>
  import('@/pages/PlatformSpacesPage').then((m) => ({ default: m.PlatformSpacesPage })),
);
const MailPage = lazy(() => import('@/pages/MailPage').then((m) => ({ default: m.MailPage })));
const MailboxPage = lazy(() =>
  import('@/pages/MailboxPage').then((m) => ({ default: m.MailboxPage })),
);
const PlatformMailPage = lazy(() =>
  import('@/pages/PlatformMailPage').then((m) => ({ default: m.PlatformMailPage })),
);
const NoAccessPage = lazy(() =>
  import('@/pages/NoAccessPage').then((m) => ({ default: m.NoAccessPage })),
);
const MobileSpaceVoicePage = lazy(() =>
  import('@/pages/MobileSpaceVoicePage').then((m) => ({ default: m.MobileSpaceVoicePage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);

function RouteLoader() {
  return (
    <div className="flex h-full min-h-48 items-center justify-center">
      <BrandLoader label="Loading page" />
    </div>
  );
}

export function App() {
  const { user, role, features, disabledFeatures, isPlatformAdmin, loading, bootstrap } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const product = effectiveProductSurface(getProductSurface(), features);
  const fullAccess = hasFullAccess(features, disabledFeatures);
  const productLanding = productHome(product);
  const home = features.length === 0 ? '/no-access' : productLanding === '/' ? featureHome(features) : productLanding;
  // Guest meeting links and the client portal both serve people with no
  // workspace account, so both skip the workspace session entirely.
  const isGuestMeetingPath =
    location.pathname.startsWith('/join/meeting/') || /^\/client(\/|$)/.test(location.pathname);

  // Resolves the zone every timestamp renders in, and — for accounts still on
  // auto-detect — pushes the device's zone up so server-sent reminders land at
  // the right hour too.
  useTimezoneSync();

  useEffect(() => {
    if (isGuestMeetingPath) return;
    void bootstrap();
  }, [bootstrap, isGuestMeetingPath]);

  useEffect(
    () => onUnauthorized(() => !isGuestMeetingPath && navigate('/login')),
    [isGuestMeetingPath, navigate],
  );

  // Guest links must never wait for, refresh, or redirect through workspace
  // authentication. This also handles phones carrying an expired saved login.
  if (isGuestMeetingPath) {
    return (
      <ConfirmProvider>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/join/meeting/:inviteToken" element={<GuestMeetingPage />} />
            <Route path="/client" element={<ClientPortalPage />} />
            <Route path="/client/:linkToken" element={<ClientPortalPage />} />
          </Routes>
        </Suspense>
        <Toaster />
      </ConfirmProvider>
    );
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <BrandLoader label="Loading your workspace" />
      </div>
    );
  }

  return (
    <ConfirmProvider>
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {!user ? (
            <>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LandingPage initialPane="login" />} />
              <Route path="/register" element={<LandingPage initialPane="register" />} />
              {/* Anything else a logged-out visitor lands on is the front door
                  rather than a bare form — they may never have seen either. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {product === 'lab' && <Route path="/mobile/space/:spaceId/voice" element={<MobileSpaceVoicePage />} />}
              <Route element={<AppShell />}>
                {product === 'lab' && features.includes('home') && <Route path="/" element={<DashboardPage />} />}
                {product === 'lab' && !features.includes('home') && <Route path="/" element={<Navigate to={home} replace />} />}
                {product !== 'lab' && <Route path="/" element={<Navigate to={home} replace />} />}
                {product === 'lab' && features.includes('spaces') && <>
                <Route path="/spaces" element={<SpacesPage />} />
                <Route path="/spaces/:spaceId" element={<SpacePage />} />
                </>}
                {product === 'lab' && features.includes('tasks') && <>
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/repositories" element={<RepositoriesPage />} />
                <Route path="/projects/:projectId" element={<BoardPage />} />
                <Route path="/my-tasks" element={<MyTasksPage />} />
                <Route path="/tasks/:taskId" element={<MyTasksPage />} />
                </>}
                {/* Rules are written about tasks and about floors now, so
                    either product is enough to open the page. */}
                {product === 'lab' && (features.includes('tasks') || features.includes('spaces')) && (
                  <Route path="/automations" element={<AutomationsPage />} />
                )}
                {(product === 'chat' || (product === 'lab' && features.includes('chat'))) && <>
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/chat/:channelId" element={<ChatPage />} />
                <Route path="/call/:callId" element={<CallRoomPage />} />
                </>}
                {product === 'lab' && fullAccess && <>
                <Route path="/inbox" element={<InboxPage />} />
                {/* The inbox opened on its reminders section: where a due
                    reminder's notification lands, and what home links to. */}
                <Route path="/reminders" element={<InboxPage />} />
                <Route path="/files" element={<FilesPage />} />
                <Route path="/whiteboards" element={<WhiteboardsPage />} />
                <Route path="/whiteboards/:boardId" element={<WhiteboardPage />} />
                </>}
                {(product === 'meet' || (product === 'lab' && features.includes('meetings'))) && <>
                {/* Two views of the same records: the calendar reads the
                    month, the meetings page administers it. */}
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/meetings" element={<MeetingsPage />} />
                <Route path="/meetings/:meetingId/room" element={<MeetingRoomPage />} />
                </>}
                {product === 'lab' && features.includes('games') && <>
                <Route path="/games" element={<GamesPage />} />
                <Route path="/games/solo/:kind" element={<SoloGamePage />} />
                <Route path="/games/:gameId" element={<GamePage />} />
                </>}
                {/* Streak nudge notifications link straight here. */}
                {product === 'lab' && features.includes('engagement') && (
                  <>
                    <Route path="/engagement" element={<EngagementPage />} />
                    {/* Where the Friday "your week" notification lands. */}
                    <Route path="/engagement/recap" element={<RecapPage />} />
                  </>
                )}
                {product === 'lab' && features.includes('learning') && (
                  <Route path="/learning" element={<LearningPage />} />
                )}
                {product === 'lab' && features.includes('insights') && (
                  <Route path="/insights" element={<InsightsPage />} />
                )}
                {/* Hosted email hangs off its own feature for the same reason
                    Company does — it is granted per person rather than being
                    one of the products an account is scoped to. The workspace
                    switch is what decides whether it exists at all. */}
                {product === 'lab' && features.includes('mail') && (
                  <>
                    {/* The mailbox is what most people come here for, so it
                        holds the address the sidebar points at; setting up a
                        domain is a thing an admin does once and is a click
                        away from it. */}
                    <Route path="/mail" element={<MailboxPage />} />
                    <Route path="/mail/setup" element={<MailPage />} />
                  </>
                )}
                {/* Company is granted per person like a product, but it is not
                    one of the products an account is scoped to — so it hangs
                    off its own feature rather than off full access. */}
                {product === 'lab' && features.includes('company') && (
                  <Route path="/company" element={<CompanyPage />} />
                )}
                {product === 'lab' && fullAccess && <>
                <Route path="/people" element={<PeoplePage />} />
                {/* Nothing on the Team page is readable without the reports
                    permission, so the route follows the same line the sidebar
                    link does rather than rendering a page of 403s. */}
                {role && can(role, 'report:view') && (
                  <Route path="/team" element={<TeamPage />} />
                )}
                </>}
                {/* Your name, your password, your notifications and the way
                    out. None of it is a workspace product, and the link to it
                    sits in the account menu unconditionally — so gating the
                    route on the full-workspace surface, or on the lab
                    hostname, only ever meant a menu item that bounced the
                    person back to where they started. */}
                <Route path="/settings" element={<SettingsPage />} />
                {product === 'lab' && role === 'owner' && <Route path="/admin" element={<AdminPage />} />}
                {/* Platform-wide, so it is not gated on a role inside one workspace. */}
                {isPlatformAdmin && (
                  <>
                    <Route path="/admin/workspaces" element={<WorkspaceRequestsPage />} />
                    <Route path="/admin/workspaces/all" element={<PlatformWorkspacesPage />} />
                    <Route path="/admin/spaces" element={<PlatformSpacesPage />} />
                    <Route path="/admin/mail" element={<PlatformMailPage />} />
                  </>
                )}
                <Route path="/no-access" element={<NoAccessPage />} />
                <Route path="*" element={<Navigate to={home} replace />} />
              </Route>
            </>
          )}
        </Routes>
      </Suspense>
      <Toaster />
    </ConfirmProvider>
  );
}
